import {
  useWixSdk as useDefaultSdk,
  type IWixStatic,
  type IPublicDataScope,
  type IConsentPolicy,
} from '@wix/yoshi-flow-editor';

type Public = IWixStatic['Data']['Public'];
type Dimensions = {
  height: number;
  width: number;
};

const PAGE_ROUTE = {
  LOBBY: '/',
  CONNECT_ACCOUNT: 'connect-account',
  MANAGE_AD_UNITS: 'manage-ad-units',
  AD_UNIT: 'ad-unit',
  AD_UNIT_EDIT: 'ad-unit/edit',
} as const;

export type PageRoute = (typeof PAGE_ROUTE)[keyof typeof PAGE_ROUTE];

export const useWixSdk = () => {
  const { Wix } = useDefaultSdk();

  // Safely access Data.Public methods - these may not be available in all contexts (e.g., Wix Studio Widget)
  const dataPublicGet: Public['get'] | undefined = Wix?.Data?.Public?.get;
  const dataPublicSet: Public['set'] | undefined = Wix?.Data?.Public?.set;
  const dataPublicRemove: Public['remove'] | undefined =
    Wix?.Data?.Public?.remove;

  function getViewMode() {
    return Wix?.Utils?.getViewMode() ?? 'editor';
  }

  function getCurrentConsentPolicy(): IConsentPolicy | undefined {
    return Wix?.Utils?.getCurrentConsentPolicy();
  }

  function getInstanceId(): string {
    return Wix?.Utils?.getInstanceId() ?? '';
  }

  function getOrigCompId(): string {
    return Wix?.Utils?.getOrigCompId() ?? '';
  }

  function refreshAppByCompIds(...args: string[]): void {
    Wix?.Settings?.refreshAppByCompIds?.(args);
  }

  function openModal(url: string, onClose: () => void): void {
    Wix?.Settings?.openModal?.(url, '100%', '100%', '', onClose);
  }

  function resizeComponent(width: number, height: number): void {
    Wix?.resizeComponent?.({ width, height });
  }

  function getLocale(): string {
    return Wix?.Utils.getLocale() || 'en';
  }

  function getInstanceValue(
    key: 'metaSiteId' | 'biToken' = 'metaSiteId',
  ): string {
    return Wix?.Utils.getInstanceValue(key) ?? '';
  }

  function getIsMobile(): boolean {
    return Boolean(Wix?.Utils.getDeviceType() === 'mobile');
  }

  function openDashboardModal(
    pageRoute: PageRoute,
    biCallback?: () => void,
    id?: string,
  ): void {
    const msid = getInstanceValue();
    const baseUrl = `https://manage.wix.com/dashboard/${msid}/google-adsense`;
    const url = `${baseUrl}/${pageRoute}${id ? `/${id}` : ''}`;
    biCallback?.();
    const onClose = () => {
      refreshAppByCompIds(getOrigCompId());
      window.location.reload();
    };
    openModal(url, onClose);
  }

  function getWixDataPublic(
    key: string = getOrigCompId(),
    scope: IPublicDataScope = 'COMPONENT',
  ) {
    return new Promise<Dimensions>((resolve, reject) => {
      if (!dataPublicGet) {
        reject(new Error('dataPublicGet not available'));
        return;
      }
      const onSuccess = (data: { [key: string]: any }) => {
        console.log('>>>> getWixDataPublic : ', data);
        resolve(data[key] as Dimensions);
      };
      const onFailure = (error: Error) => {
        reject(new Error(`dataPublicGet : ${error?.message ?? ''}`));
      };
      dataPublicGet(key, { scope }, onSuccess, onFailure);
    });
  }

  const setWixDataPublic = (
    key: string = getOrigCompId(),
    scope: IPublicDataScope = 'COMPONENT',
    data: Dimensions,
  ) => {
    return new Promise<Dimensions>((resolve, reject) => {
      if (!dataPublicSet) {
        reject(new Error('dataPublicSet not available'));
        return;
      }
      const onSuccess = (data: { [key: string]: any }) => {
        console.log('>>>> setWixDataPublic : ', data);
        resolve(data[key] as Dimensions);
      };
      const onFailure = (error: Error) => {
        reject(new Error(`dataPublicSet : ${error?.message ?? ''}`));
      };
      dataPublicSet(key, data, { scope }, onSuccess, onFailure);
    });
  };

  const removeWixDataPublic = (
    key: string = getOrigCompId(),
    scope: IPublicDataScope = 'COMPONENT',
  ) => {
    return new Promise<Dimensions>((resolve, reject) => {
      if (!dataPublicRemove) {
        reject(new Error('dataPublicRemove not available'));
        return;
      }
      const onSuccess = (removedKey: { [key: string]: any }) => {
        console.log('>>>> removeWixDataPublic : ', removedKey);
        resolve(removedKey[key] as Dimensions);
      };
      const onFailure = (error: Error) => {
        reject(new Error(`dataPublicRemove : ${error?.message ?? ''}`));
      };
      dataPublicRemove(key, { scope }, onSuccess, onFailure);
    });
  };

  return {
    getOrigCompId,
    refreshAppByCompIds,
    openModal,
    openDashboardModal,
    getLocale,
    getIsMobile,
    getInstanceId,
    getWixDataPublic,
    setWixDataPublic,
    removeWixDataPublic,
    getViewMode,
    getInstanceValue,
    getCurrentConsentPolicy,
    resizeComponent,
  };
};
